import { ref } from 'vue'
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'

// Cancelling a check-in/check-out movement also cancels its paired twin (see
// WarehouseMovement.find_twin on the backend): a departure that never happened shouldn't
// leave its return pending, and vice versa. Warn the user, naming the twin, before proceeding.
export function useWarehouseMovementCancel(refreshAsync) {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const { SCHEDULED, CANCELLED } = planningStore.warehouseMovementStatusEnum

  const showConfirm = ref(false)
  const confirmMessage = ref('')
  const pendingMovement = ref(null)

  const cancelAsync = async(movement) => {
    await planningStore.upsertWarehouseMovementAsync({ id: movement.id, status: CANCELLED })
    await refreshAsync()
  }

  const requestCancel = async(movement) => {
    const twin = await planningStore.getWarehouseMovementTwinAsync(movement.id)
    if (twin && twin.status === SCHEDULED) {
      pendingMovement.value = movement
      const typeLabel = planningStore.getLocalizedWarehouseMovementTypes().find(t => t.id === twin.movement_type)?.name
      confirmMessage.value = i18n.t('warning.cancelTwinMovementConfirm', {
        type: typeLabel,
        date: dayjs(twin.scheduled_at).format('DD/MM'),
      })
      showConfirm.value = true
      return
    }
    await cancelAsync(movement)
  }

  const onCancelProceed = async() => {
    const movement = pendingMovement.value
    pendingMovement.value = null
    if (!movement) return
    await cancelAsync(movement)
  }

  const onCancelDismiss = () => { pendingMovement.value = null }

  return { showConfirm, confirmMessage, requestCancel, onCancelProceed, onCancelDismiss }
}
